import React from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Check, Plus, Star, Crown, Handshake, X } from 'lucide-react';
import { RARITIES, VARIANTS, MAX_LEVEL } from '../mock';

function LevelDotsLarge({ level, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: MAX_LEVEL }).map((_, i) => {
        const filled = i < level;
        return (
          <button
            key={i}
            onClick={(e) => {
              e.stopPropagation();
              if (filled && i + 1 === level) onChange(level - 1);
              else onChange(i + 1);
            }}
            className={`h-9 w-9 rounded-lg flex items-center justify-center transition-transform hover:scale-110 border-2 ${
              filled
                ? 'bg-yellow-300 text-amber-900 border-[#3a1064] shadow-[0_2px_0_#3a1064]'
                : 'bg-white text-slate-300 border-slate-200 hover:border-purple-300'
            }`}
            aria-label={`Set level ${i + 1}`}
            data-testid={`detail-level-dot-${i + 1}`}
          >
            <Star className="h-5 w-5" fill={filled ? 'currentColor' : 'none'} strokeWidth={2.5} />
          </button>
        );
      })}
    </div>
  );
}

export default function SpriteDetailDialog({
  sprite,
  entry,
  open,
  onOpenChange,
  onToggle,
  onLevel,
  onMaster,
  onTrade,
}) {
  if (!sprite) return null;
  const rarity = RARITIES[sprite.rarity];
  const variant = VARIANTS[sprite.variant];
  const owned = !!entry?.owned;
  const level = entry?.level || 0;
  const mastered = !!entry?.mastered;

  const handleTrade = () => {
    onOpenChange(false);
    setTimeout(() => onTrade && onTrade(sprite), 150);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[420px] w-[calc(100vw-16px)] max-h-[92vh] p-0 border-[4px] rounded-2xl overflow-y-auto gap-0"
        style={{
          background: 'var(--t-card)',
          borderColor: mastered ? '#ffb800' : 'var(--t-card-border)',
          boxShadow: mastered
            ? '0 8px 0 var(--t-card-border), 0 0 32px rgba(255,184,0,0.7)'
            : '0 8px 0 var(--t-card-border)',
        }}
        data-testid="sprite-detail-dialog"
      >
        {/* Top color bar */}
        <div
          className="h-2.5 w-full"
          style={{
            background: mastered
              ? 'linear-gradient(90deg,#ff2bd6,#ffd900,#00b8ff,#9d3dff,#2ee6c1,#ff2bd6)'
              : `linear-gradient(90deg, ${rarity.color}, ${variant.color})`,
            backgroundSize: mastered ? '300% 100%' : '100% 100%',
            animation: mastered ? 'rainbowSlide 4s linear infinite' : 'none',
          }}
        />

        {/* Image area */}
        <div
          className="relative aspect-square flex items-center justify-center"
          style={{
            background: mastered
              ? `radial-gradient(circle at center, #ffd90055 0%, transparent 65%), linear-gradient(135deg, #ffd900 0%, #ff2bd6 50%, #00b8ff 100%)`
              : owned
              ? `radial-gradient(circle at center, ${rarity.color}44 0%, transparent 70%), linear-gradient(180deg, ${variant.color}33 0%, ${rarity.color}26 100%)`
              : `radial-gradient(circle at center, ${rarity.color}26 0%, transparent 70%), linear-gradient(180deg, ${variant.color}1a 0%, ${rarity.color}14 100%)`,
          }}
        >
          {/* close button */}
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/90 text-[#3a1064] flex items-center justify-center shadow-[0_2px_0_#3a1064] border-2 border-[#3a1064] z-10 hover:bg-white"
            aria-label="Close"
            data-testid="detail-close-btn"
          >
            <X className="h-4 w-4" strokeWidth={3} />
          </button>

          {/* variant tag */}
          <span
            className="absolute top-3 left-3 text-xs font-black uppercase tracking-wider px-3 py-1 rounded-md text-white border border-white/40 shadow-[0_2px_0_rgba(0,0,0,0.25)]"
            style={{ background: variant.color }}
          >
            {variant.label}
          </span>

          {/* checkmark badge (when owned but not mastered) */}
          {owned && !mastered && (
            <div className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-[0_2px_0_#0c5132] ring-2 ring-white">
              <Check className="h-5 w-5" strokeWidth={3} />
            </div>
          )}
          {mastered && (
            <div className="absolute bottom-3 right-3 h-12 w-12 rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 text-amber-900 flex items-center justify-center shadow-[0_2px_0_#7a4c00] ring-2 ring-white animate-pulse">
              <Crown className="h-6 w-6" strokeWidth={3} fill="currentColor" />
            </div>
          )}

          <img
            src={sprite.img}
            alt={sprite.name}
            className={`h-[78%] w-[78%] object-contain transition-all duration-300 ${
              owned ? 'scale-100 drop-shadow-[0_6px_8px_rgba(58,16,100,0.35)]' : 'grayscale opacity-50'
            } ${mastered ? 'drop-shadow-[0_0_18px_rgba(255,255,255,0.9)]' : ''}`}
            onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
          />
        </div>

        {/* Footer */}
        <div className="p-5" style={{ background: 'var(--t-card)' }}>
          <div className="font-black text-xl leading-tight" style={{ color: 'var(--t-text)' }} data-testid="detail-sprite-name">
            {sprite.name}
          </div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span
              className="text-[11px] font-black uppercase px-2 py-0.5 rounded text-white"
              style={{ background: rarity.color }}
            >
              {rarity.label}
            </span>
            <span className="text-sm font-bold" style={{ color: 'var(--t-text-soft)' }}>{sprite.drop}</span>
            {sprite.tag && (
              <span className="text-[11px] font-black uppercase px-2 py-0.5 rounded bg-cyan-400 text-white">
                {sprite.tag}
              </span>
            )}
          </div>

          {/* Level row */}
          <div className="mt-5 flex items-center justify-between gap-2">
            <LevelDotsLarge level={level} onChange={(lvl) => onLevel(sprite.id, lvl)} />
            <span className="text-sm font-black whitespace-nowrap leading-tight text-right" style={{ color: 'var(--t-text)' }}>
              Lv<br />{level}/{MAX_LEVEL}
            </span>
          </div>

          {/* Owned + Mastered */}
          <div className="mt-5 grid grid-cols-2 gap-2">
            <button
              onClick={() => onToggle(sprite.id)}
              data-testid="detail-owned-btn"
              className={`h-11 rounded-xl text-sm font-black uppercase tracking-wide transition-all flex items-center justify-center gap-2 border-2 shadow-[0_3px_0_var(--t-card-border)] ${
                owned ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'hover:opacity-90'
              }`}
              style={{
                borderColor: 'var(--t-card-border)',
                background: owned ? undefined : 'var(--t-card)',
                color: owned ? undefined : 'var(--t-text)',
              }}
            >
              {owned ? <Check className="h-4 w-4" strokeWidth={3} /> : <Plus className="h-4 w-4" strokeWidth={3} />}
              Owned
            </button>
            <button
              onClick={() => onMaster(sprite.id)}
              data-testid="detail-mastered-btn"
              className={`h-11 rounded-xl text-sm font-black uppercase tracking-wide transition-all flex items-center justify-center gap-2 border-2 shadow-[0_3px_0_var(--t-card-border)] ${
                mastered ? 'bg-gradient-to-r from-yellow-300 via-pink-400 to-cyan-400 text-[#3a1064]' : 'hover:opacity-90'
              }`}
              style={{
                borderColor: 'var(--t-card-border)',
                background: mastered ? undefined : 'var(--t-card)',
                color: mastered ? undefined : 'var(--t-text)',
              }}
            >
              <Crown className="h-4 w-4" strokeWidth={3} fill={mastered ? 'currentColor' : 'none'} />
              Mastered
            </button>
          </div>

          {/* Trade */}
          <button
            onClick={handleTrade}
            data-testid="detail-trade-btn"
            className="mt-3 w-full h-11 rounded-xl text-sm font-black uppercase tracking-wide transition-all flex items-center justify-center gap-2 border-2 bg-[#ff2bd6] hover:bg-[#e119bf] text-white shadow-[0_3px_0_var(--t-card-border)]"
            style={{ borderColor: 'var(--t-card-border)' }}
          >
            <Handshake className="h-4 w-4" strokeWidth={3} />
            Trade
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
